import React, { useState, useEffect } from 'react';
import { Calendar, ArrowRight, RefreshCw, Clock, ExternalLink, TrendingUp } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { mockData } from '../data/mockData';
import chinaExpoNewsService from '../services/chinaExpoNewsService';
import industryInsightsService from '../services/industryInsightsService';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import EditableText from './EditableText';

const BlogSection = () => {
  const [chinaExpoNews, setChinaExpoNews] = useState([]);
  const [industryInsights, setIndustryInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nextUpdateInfo, setNextUpdateInfo] = useState('');
  const [insightsUpdateInfo, setInsightsUpdateInfo] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Check if user is admin on component mount
  useEffect(() => {
    const adminStatus = localStorage.getItem('isAdminAuthenticated') === 'true';
    setIsAdmin(adminStatus);
  }, []);

  // Load both China EXPO news and Industry Insights on component mount
  useEffect(() => {
    loadAllContent();
    updateNextUpdateInfo();
    
    // Set up interval to update the "next update" info every hour
    const interval = setInterval(updateNextUpdateInfo, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Load Soro blog embed script
  useEffect(() => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src*="trysoro.com"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://app.trysoro.com/api/embed/b3cc586f-e86b-460d-a00a-c3cbf367d05a';
      script.defer = true;
      script.async = true;
      document.body.appendChild(script);
      
      return () => {
        // Cleanup script on unmount
        const scriptToRemove = document.querySelector('script[src*="trysoro.com"]');
        if (scriptToRemove) {
          scriptToRemove.remove();
        }
      };
    }
  }, []);

  // Auto-scroll carousel for Soro blog (every 5 seconds)
  useEffect(() => {
    let scrollInterval;
    let currentIndex = 0;
    
    // Wait for Soro content to load, then start auto-scroll
    const startAutoScroll = () => {
      // Try multiple selectors to find the scrollable list
      const soroContainer = document.getElementById('soro-blog');
      if (!soroContainer) {
        setTimeout(startAutoScroll, 1000);
        return;
      }
      
      // Find any scrollable container inside soro-blog
      let soroList = soroContainer.querySelector('.soro-blog-list') ||
                     soroContainer.querySelector('[class*="list"]') ||
                     soroContainer.querySelector('[class*="List"]') ||
                     soroContainer.querySelector('[class*="grid"]') ||
                     soroContainer.querySelector('[class*="Grid"]') ||
                     soroContainer.querySelector('ul') ||
                     soroContainer.querySelector('div > div');
      
      if (!soroList) {
        // Retry after 1 second if Soro hasn't loaded yet
        setTimeout(startAutoScroll, 1000);
        return;
      }
      
      const items = soroList.children;
      if (items.length <= 1) return; // No need to scroll if only one item
      
      scrollInterval = setInterval(() => {
        currentIndex = (currentIndex + 1) % items.length;
        const scrollAmount = soroList.clientWidth * currentIndex;
        soroList.scrollTo({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }, 5000); // 5 seconds
    };
    
    // Start after a delay to let Soro embed load
    const initTimeout = setTimeout(startAutoScroll, 2000);
    
    return () => {
      clearTimeout(initTimeout);
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, []);

  // Lock body scroll when Soro article is open
  useEffect(() => {
    let savedScrollPosition = 0;
    let isArticleOpen = false;
    
    // Capture scroll position BEFORE Soro opens article (on click)
    const handleClick = (e) => {
      // Don't capture scroll if article is already open (would overwrite with 0)
      if (document.body.classList.contains('soro-article-open')) {
        return;
      }
      // Check if click is on a Soro card or read more button
      const card = e.target.closest('.soro-blog-card, [class*="card"], a, button');
      if (card) {
        savedScrollPosition = window.scrollY;
      }
    };
    
    const observer = new MutationObserver((mutations) => {
      const soroArticle = document.querySelector('#soro-blog .soro-blog-article');
      
      if (soroArticle && !isArticleOpen) {
        // Article just opened - lock body scroll
        isArticleOpen = true;
        document.body.classList.add('soro-article-open');
        document.body.style.top = `-${savedScrollPosition}px`;
      } else if (!soroArticle && isArticleOpen) {
        // Article just closed - restore body scroll
        isArticleOpen = false;
        document.body.classList.remove('soro-article-open');
        document.body.style.top = '';
        window.scrollTo(0, savedScrollPosition);
      }
    });
    
    // Start observing when Soro embed loads
    const startObserving = () => {
      const soroContainer = document.getElementById('soro-blog');
      if (soroContainer) {
        // Add click listener to capture scroll before Soro navigation
        soroContainer.addEventListener('click', handleClick, true);
        
        observer.observe(soroContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
      } else {
        setTimeout(startObserving, 500);
      }
    };
    
    setTimeout(startObserving, 1000);
    
    return () => {
      observer.disconnect();
      const soroContainer = document.getElementById('soro-blog');
      if (soroContainer) {
        soroContainer.removeEventListener('click', handleClick, true);
      }
      document.body.classList.remove('soro-article-open');
      document.body.style.top = '';
    };
  }, []);

  // Soro UI text localization - Force English for specific UI elements only
  // (Does not affect article content, only: back button, loading text, date format)
  useEffect(() => {
    // Serbian Cyrillic month names to English mapping
    const monthMap = {
      'јануар': 'January', 'фебруар': 'February', 'март': 'March',
      'април': 'April', 'мај': 'May', 'јун': 'June',
      'јул': 'July', 'август': 'August', 'септембар': 'September',
      'октобар': 'October', 'новембар': 'November', 'децембар': 'December'
    };

    const localizeText = (element) => {
      if (!element) return;
      
      // 1. Back button: "Сви чланци" → "All Articles"
      const backButtons = element.querySelectorAll('.soro-blog-back, [class*="back"]');
      backButtons.forEach(btn => {
        if (btn.textContent.includes('Сви чланци') || btn.textContent.includes('чланци')) {
          btn.textContent = '← All Articles';
        }
      });

      // 2. Loading text: "Учитавање" → "Loading..."
      const allElements = element.querySelectorAll('*');
      allElements.forEach(el => {
        if (el.children.length === 0 && el.textContent) {
          const text = el.textContent.trim();
          if (text === 'Учитавање' || text === 'Učitavanje' || text.includes('Учитавање')) {
            el.textContent = 'Loading...';
          }
        }
      });

      // 3. Date format: Serbian Cyrillic months → English
      const dateElements = element.querySelectorAll('time, [class*="date"], span, p');
      dateElements.forEach(el => {
        if (el.children.length === 0 && el.textContent) {
          let text = el.textContent;
          let changed = false;
          Object.entries(monthMap).forEach(([sr, en]) => {
            if (text.includes(sr)) {
              text = text.replace(sr, en);
              changed = true;
            }
          });
          if (changed) {
            el.textContent = text;
          }
        }
      });
    };

    const observer = new MutationObserver((mutations) => {
      const soroContainer = document.getElementById('soro-blog');
      if (soroContainer) {
        localizeText(soroContainer);
      }
    });

    const startObserving = () => {
      const soroContainer = document.getElementById('soro-blog');
      if (soroContainer) {
        // Initial pass
        localizeText(soroContainer);
        // Watch for dynamic content changes
        observer.observe(soroContainer, {
          childList: true,
          subtree: true,
          characterData: true
        });
      } else {
        setTimeout(startObserving, 500);
      }
    };

    setTimeout(startObserving, 1500);

    return () => observer.disconnect();
  }, []);

  const loadAllContent = async () => {
    setIsLoading(true);
    try {
      const [expoNews, insights] = await Promise.all([
        chinaExpoNewsService.getLatestNews(),
        industryInsightsService.getLatestInsights()
      ]);
      setChinaExpoNews(expoNews);
      setIndustryInsights(insights);
    } catch (error) {
      console.error('Error loading content:', error);
      toast({
        title: "Error Loading Content",
        description: "Failed to load latest content. Showing cached data.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateNextUpdateInfo = () => {
    const expoInfo = chinaExpoNewsService.getFormattedTimeUntilUpdate();
    const insightsInfo = industryInsightsService.getFormattedTimeUntilUpdate();
    setNextUpdateInfo(expoInfo);
    setInsightsUpdateInfo(insightsInfo);
  };

  const handleRefreshContent = async () => {
    setIsRefreshing(true);
    try {
      const [freshNews, freshInsights] = await Promise.all([
        chinaExpoNewsService.forceRefresh(),
        industryInsightsService.forceRefresh()
      ]);
      setChinaExpoNews(freshNews);
      setIndustryInsights(freshInsights);
      updateNextUpdateInfo();
      toast({
        title: "Content Updated",
        description: "Successfully refreshed all blog content.",
      });
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Unable to refresh content. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };
  const handleReadMore = (article) => {
    if (article.url && !article.fullContent) {
      // If there's an external URL but no full content, open in new tab
      window.open(article.url, '_blank', 'noopener,noreferrer');
    } else if (article.fullContent) {
      // If there's full content, show in modal
      setSelectedArticle(article);
      setIsModalOpen(true);
    } else {
      // Fallback - just show a message
      toast({
        title: "Article Link",
        description: "This article will open in a new tab.",
        variant: "default"
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
  };

  return (
    <section id="blog" className="py-20 lg:py-28 bg-slate-50 border-t border-slate-200/60">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header with Auto-Update Info */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="flex flex-col sm:flex-row items-center justify-center mb-4 gap-2 sm:gap-4">
            <EditableText
              section="blog"
              field="title"
              as="h2"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900"
            >
              {t('blog.title')}
            </EditableText>
            {/* Refresh button - only visible to admins */}
            {isAdmin && (
              <Button
                onClick={handleRefreshContent}
                disabled={isRefreshing}
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-800"
                title="Admin Only: Refresh blog content"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? t('blog.updating') : t('blog.refreshAll')}
              </Button>
            )}
          </div>
          
          <EditableText
            section="blog"
            field="description"
            as="p"
            className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto mb-4 px-4"
          >
            {t('blog.description')}
          </EditableText>
          
          <div className="w-24 h-1 bg-blue-600 mx-auto mt-6"></div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading latest content...</p>
          </div>
        )}

        {/* China EXPO News Grid */}
        {!isLoading && chinaExpoNews.length > 0 && (
          <div className="mb-12 sm:mb-16">
            <div className="text-center mb-6 sm:mb-8">
              <EditableText
                section="blog"
                field="chinaExpoNewsTitle"
                as="h3"
                className="text-2xl font-bold text-blue-600 mb-2"
              >
                🇨🇳 {t('blog.chinaExpoNews')}
              </EditableText>
              <EditableText
                section="blog"
                field="expoDescription"
                as="p"
                className="text-slate-600 text-sm sm:text-base"
              >
                {t('blog.expoDescription')}
              </EditableText>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {chinaExpoNews.slice(0, 3).map((article) => (
                <article
                  key={article.id}
                  className="bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md transition-all duration-300 group"
                  data-testid={`expo-article-${article.id}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {article.category}
                    </div>
                    <div className="absolute top-4 right-4 bg-white bg-opacity-90 text-gray-700 px-2 py-1 rounded text-xs">
                      {article.readTime}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Date and Source */}
                    <div className="flex items-center justify-between text-gray-500 text-sm mb-3">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        {article.date}
                      </div>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">{article.source}</span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-blue-900 mb-3 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
                      {article.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>

                    {/* Read More */}
                    <button 
                      onClick={() => handleReadMore(article)}
                      className="flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-300 group-hover:translate-x-2 transform"
                    >
                      <EditableText section="blog" field="readMoreButton" as="span">
                        {t('blog.readMore')}
                      </EditableText>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Soro Publishing Blog - Horizontal Carousel */}
        <div className="mb-12 sm:mb-16">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-purple-600 mb-2">
              {t('blog.dailyArticles')}
            </h3>
          </div>
          
          {/* Constrained width container to match other blog sections */}
          <div className="max-w-5xl mx-auto">
            <div className="relative overflow-hidden rounded-lg sm:rounded-xl bg-white shadow-lg border-2 border-purple-100 mx-2 sm:mx-0">
              {/* Carousel Container - Responsive */}
              <div 
                id="soro-blog-wrapper"
                className="soro-carousel-container"
              >
                <div id="soro-blog" className="soro-blog-content"></div>
              </div>
              
              {/* Carousel Navigation Dots */}
              <div className="flex justify-center space-x-2 py-3 sm:py-4 bg-gray-50">
                <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></span>
                <span className="w-2 h-2 rounded-full bg-purple-300"></span>
                <span className="w-2 h-2 rounded-full bg-purple-300"></span>
              </div>
            </div>
          </div>
          
          {/* Responsive Carousel Styles */}
          <style>{`
            /* Container - responsive height based on viewport */
            .soro-carousel-container {
              scroll-behavior: smooth;
              position: relative;
              overflow: hidden;
              padding: 0.25rem;
              height: auto;
              min-height: 180px;
              max-height: 280px;
            }
            
            /* Small phones (320px - 479px) */
            @media (min-width: 320px) and (max-width: 479px) {
              .soro-carousel-container {
                padding: 0.25rem;
                min-height: 160px;
                max-height: 260px;
              }
            }
            
            /* Larger phones (480px - 639px) */
            @media (min-width: 480px) and (max-width: 639px) {
              .soro-carousel-container {
                padding: 0.375rem;
                min-height: 180px;
                max-height: 280px;
              }
            }
            
            /* Small tablets (640px - 767px) */
            @media (min-width: 640px) and (max-width: 767px) {
              .soro-carousel-container {
                padding: 0.5rem;
                min-height: 200px;
                max-height: 300px;
              }
            }
            
            /* Tablets (768px - 1023px) */
            @media (min-width: 768px) and (max-width: 1023px) {
              .soro-carousel-container {
                padding: 0.75rem;
                min-height: 240px;
                max-height: 320px;
              }
            }
            
            /* Desktop (1024px+) */
            @media (min-width: 1024px) {
              .soro-carousel-container {
                padding: 0.75rem;
                min-height: 140px;
                max-height: 180px;
              }
            }
            
            #soro-blog {
              width: 100%;
              height: 100%;
              overflow: hidden;
            }
            
            /* Target Soro list container for horizontal layout */
            #soro-blog .soro-blog-list {
              display: flex !important;
              flex-direction: row !important;
              flex-wrap: nowrap !important;
              overflow-x: auto !important;
              overflow-y: hidden !important;
              scroll-snap-type: x mandatory !important;
              scroll-behavior: smooth !important;
              -webkit-overflow-scrolling: touch !important;
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
              gap: 0 !important;
              padding: 0 !important;
              height: 100% !important;
              width: 100% !important;
            }
            
            #soro-blog .soro-blog-list::-webkit-scrollbar {
              display: none !important;
            }
            
            /* Each article card takes FULL width - one at a time */
            #soro-blog .soro-blog-card {
              flex: 0 0 100% !important;
              min-width: 100% !important;
              max-width: 100% !important;
              width: 100% !important;
              scroll-snap-align: center !important;
              scroll-snap-stop: always !important;
              height: auto !important;
              max-height: 100% !important;
              padding: 0.25rem !important;
              box-sizing: border-box !important;
              overflow: hidden !important;
            }
            
            /* Desktop card layout - horizontal with image on left */
            @media (min-width: 1024px) {
              #soro-blog .soro-blog-card {
                display: flex !important;
                flex-direction: row !important;
                align-items: center !important;
                padding: 0.5rem 1rem !important;
                gap: 1rem !important;
                max-height: 160px !important;
              }
              #soro-blog .soro-blog-card img {
                width: 140px !important;
                min-width: 140px !important;
                max-width: 140px !important;
                height: 100px !important;
                object-fit: cover !important;
                border-radius: 8px !important;
                flex-shrink: 0 !important;
              }
              #soro-blog .soro-blog-card > div,
              #soro-blog .soro-blog-card > a > div {
                flex: 1 !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: center !important;
                overflow: hidden !important;
              }
              #soro-blog .soro-blog-card h3,
              #soro-blog .soro-blog-card h4 {
                font-size: 1rem !important;
                line-height: 1.3 !important;
                margin-bottom: 0.25rem !important;
                display: -webkit-box !important;
                -webkit-line-clamp: 2 !important;
                -webkit-box-orient: vertical !important;
                overflow: hidden !important;
              }
              #soro-blog .soro-blog-card p {
                font-size: 0.85rem !important;
                line-height: 1.4 !important;
                display: -webkit-box !important;
                -webkit-line-clamp: 2 !important;
                -webkit-box-orient: vertical !important;
                overflow: hidden !important;
                margin-bottom: 0.25rem !important;
              }
              #soro-blog .soro-blog-card span,
              #soro-blog .soro-blog-card time {
                font-size: 0.75rem !important;
              }
            }
            
            /* Card content scaling for mobile */
            @media (max-width: 479px) {
              #soro-blog .soro-blog-card {
                padding: 0.125rem !important;
              }
              #soro-blog .soro-blog-card * {
                font-size: 0.875rem !important;
                line-height: 1.3 !important;
              }
              #soro-blog .soro-blog-card img {
                max-height: 120px !important;
                object-fit: cover !important;
              }
            }
            
            /* Card content scaling for tablets */
            @media (min-width: 480px) and (max-width: 767px) {
              #soro-blog .soro-blog-card {
                padding: 0.25rem !important;
              }
              #soro-blog .soro-blog-card * {
                font-size: 0.9rem !important;
                line-height: 1.4 !important;
              }
              #soro-blog .soro-blog-card img {
                max-height: 140px !important;
                object-fit: cover !important;
              }
            }
            
            /* Lock body scroll when article is open */
            body.soro-article-open {
              overflow: hidden !important;
              position: fixed !important;
              width: 100% !important;
              height: 100% !important;
            }
            
            /* Article reading view - isolated scroll container */
            #soro-blog .soro-blog-article {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              z-index: 99999 !important;
              background: white !important;
              overflow-y: auto !important;
              overflow-x: hidden !important;
              padding: 70px 0.75rem 2rem 0.75rem !important;
              box-sizing: border-box !important;
              -webkit-overflow-scrolling: touch !important;
              overscroll-behavior: contain !important;
              touch-action: pan-y !important;
            }
            
            @media (min-width: 480px) {
              #soro-blog .soro-blog-article {
                padding: 70px 1rem 2rem 1rem !important;
              }
            }
            
            @media (min-width: 640px) {
              #soro-blog .soro-blog-article {
                padding: 70px 2rem 2rem 2rem !important;
              }
            }
            
            @media (min-width: 768px) {
              #soro-blog .soro-blog-article {
                padding: 70px 3rem 2rem 3rem !important;
              }
            }
            
            @media (min-width: 1024px) {
              #soro-blog .soro-blog-article {
                padding: 70px 15% 2rem 15% !important;
              }
            }
            
            /* Reset children to NOT inherit fixed positioning */
            #soro-blog .soro-blog-article > *,
            #soro-blog .soro-blog-article > * > * {
              position: static !important;
              height: auto !important;
              max-height: none !important;
              width: auto !important;
              overflow: visible !important;
            }
            
            /* Article content should flow naturally */
            #soro-blog .soro-blog-article-content {
              width: 100% !important;
              max-width: 100% !important;
              position: static !important;
              height: auto !important;
              overflow: visible !important;
            }
            
            /* Back button - FIXED positioning with safe area padding */
            #soro-blog .soro-blog-back {
              position: fixed !important;
              top: 16px !important;
              left: 16px !important;
              z-index: 100001 !important;
              background: #6b21a8 !important;
              color: white !important;
              padding: 10px 18px !important;
              border-radius: 8px !important;
              font-weight: 600 !important;
              font-size: 14px !important;
              cursor: pointer !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              visibility: visible !important;
              opacity: 1 !important;
              box-shadow: 0 4px 12px rgba(107, 33, 168, 0.4) !important;
              border: none !important;
              min-width: 80px !important;
              min-height: 40px !important;
              /* Safe area inset for notched phones */
              top: max(16px, env(safe-area-inset-top, 16px)) !important;
              left: max(16px, env(safe-area-inset-left, 16px)) !important;
            }
            
            /* Back button hover state */
            #soro-blog .soro-blog-back:hover {
              background: #581c87 !important;
              transform: scale(1.02) !important;
            }
            
            /* Back button on smaller phones */
            @media (max-width: 479px) {
              #soro-blog .soro-blog-back {
                top: 12px !important;
                left: 12px !important;
                padding: 8px 14px !important;
                font-size: 13px !important;
                min-width: 70px !important;
                min-height: 36px !important;
                top: max(12px, env(safe-area-inset-top, 12px)) !important;
                left: max(12px, env(safe-area-inset-left, 12px)) !important;
              }
            }
            
            /* Ensure text is readable */
            #soro-blog .soro-blog-article * {
              max-width: 100% !important;
              word-wrap: break-word !important;
              overflow-wrap: break-word !important;
            }
            
            /* Images in article view */
            #soro-blog .soro-blog-article img {
              max-width: 100% !important;
              height: auto !important;
              object-fit: contain !important;
            }
            
            /* Soro blog content wrapper */
            .soro-blog-content {
              width: 100%;
              height: 100%;
            }
          `}</style>
        </div>

        {/* Industry Insights Grid - Compact with View All */}
        {!isLoading && industryInsights.length > 0 && (
          <div className="mb-12 sm:mb-16">
            <div className="text-center mb-6 sm:mb-8">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                <EditableText
                  section="blog"
                  field="industryInsightsTitle"
                  as="h3"
                  className="text-xl sm:text-2xl font-bold text-blue-600"
                >
                  {t('blog.industryInsights')}
                </EditableText>
              </div>
              <EditableText
                section="blog"
                field="industryInsightsDescription"
                as="p"
                className="text-slate-600 text-sm sm:text-base"
              >
                {t('blog.industryInsightsDescription')}
              </EditableText>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {industryInsights.slice(0, 3).map((insight) => (
                <article
                  key={insight.id}
                  className="bg-white rounded-lg overflow-hidden border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-300 group"
                  data-testid={`insight-article-${insight.id}`}
                >
                  {/* Image - Compact */}
                  <div className="relative h-36 sm:h-40 overflow-hidden">
                    <img
                      src={insight.image}
                      alt={insight.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-medium">
                      {insight.category}
                    </div>
                    <div className="absolute bottom-3 right-3 bg-white/90 text-slate-600 px-2 py-0.5 rounded text-xs">
                      {insight.readTime}
                    </div>
                  </div>

                  {/* Content - Compact */}
                  <div className="p-4">
                    {/* Date */}
                    <div className="flex items-center text-slate-500 text-xs mb-2">
                      <Calendar className="w-3 h-3 mr-1" />
                      {insight.date}
                    </div>

                    {/* Title */}
                    <h4 className="text-base font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
                      {insight.title}
                    </h4>

                    {/* Read More */}
                    <button 
                      onClick={() => handleReadMore(insight)}
                      className="flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-300"
                    >
                      {t('blog.readMore')}
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {/* View All Articles Button */}
            {industryInsights.length > 3 && (
              <div className="text-center mt-8">
                <Button 
                  onClick={() => document.getElementById('blog')?.scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-2"
                  data-testid="view-all-articles-button"
                >
                  {t('blog.viewAllArticles') || 'View All Articles'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* FAQ Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 lg:p-12 mt-16">
          <div className="text-center mb-10 sm:mb-12">
            <EditableText
              section="faq"
              field="title"
              as="h3"
              className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4"
            >
              {t('faq.title').toUpperCase()}
            </EditableText>
            <EditableText
              section="faq"
              field="subtitle"
              as="h4"
              className="text-xl sm:text-2xl text-slate-700 mb-4"
            >
              {t('faq.subtitle')}
            </EditableText>
            <EditableText
              section="faq"
              field="takeAdvantage"
              as="p"
              className="text-lg text-blue-600 font-semibold"
            >
              {t('faq.takeAdvantage')}
            </EditableText>
            <EditableText
              section="faq"
              field="helpOptimize"
              as="p"
              className="text-slate-600 mt-2"
            >
              {t('faq.helpOptimize')}
            </EditableText>
          </div>

          {/* Desktop: Grid layout */}
          <div className="hidden md:grid md:grid-cols-2 gap-8">
            {t('faq.questions').map((faq, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-6" data-testid={`faq-item-${index}`}>
                <EditableText
                  section="faq"
                  field={`question${index + 1}`}
                  as="h5"
                  className="text-lg font-bold text-slate-900 mb-3"
                >
                  {faq.question}
                </EditableText>
                <EditableText
                  section="faq"
                  field={`answer${index + 1}`}
                  as="p"
                  className="text-slate-600 leading-relaxed"
                >
                  {faq.answer}
                </EditableText>
              </div>
            ))}
          </div>

          {/* Mobile: Accordion layout */}
          <div className="md:hidden space-y-3">
            {t('faq.questions').map((faq, index) => (
              <details key={index} className="group border border-slate-200 rounded-lg" data-testid={`faq-accordion-${index}`}>
                <summary className="flex items-center justify-between cursor-pointer p-4 text-slate-900 font-semibold hover:bg-slate-50 transition-colors list-none">
                  <span className="text-base pr-4">{faq.question}</span>
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-4 pb-4 text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <EditableText
              section="faq"
              field="successfulImporting"
              as="p"
              className="text-base sm:text-lg text-slate-900 font-semibold mb-6"
            >
              {t('faq.successfulImporting')}
            </EditableText>
            <Button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              data-testid="faq-cta-button"
            >
              <EditableText
                section="faq"
                field="startProjectButton"
                as="span"
              >
                {t('faq.startProject')}
              </EditableText>
            </Button>
          </div>
        </div>
      </div>

      {/* Article Modal */}
      {isModalOpen && selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl">
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span>{selectedArticle.date}</span>
                    <span>•</span>
                    <span>{selectedArticle.readTime}</span>
                    <span>•</span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                      {selectedArticle.category}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-2">{selectedArticle.title}</h2>
                  <p className="text-gray-600 text-sm">Source: {selectedArticle.source}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {selectedArticle.image && (
                <img
                  src={selectedArticle.image}
                  alt={selectedArticle.title}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}
              <div className="prose prose-lg max-w-none">
                {selectedArticle.fullContent ? (
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {selectedArticle.fullContent}
                  </div>
                ) : (
                  <p className="text-gray-600">{selectedArticle.excerpt}</p>
                )}
              </div>

              {/* External Link Button */}
              {selectedArticle.url && (
                <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-900 font-medium">Read Full Article</p>
                      <p className="text-xs text-blue-600">Visit the original source for complete coverage</p>
                    </div>
                    <button
                      onClick={() => window.open(selectedArticle.url, '_blank', 'noopener,noreferrer')}
                      className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Article
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BlogSection;